import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toLocalISODate } from "../utils/date";
import { blockedLabel } from "../utils/otaSource";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOW = ["S","M","T","W","T","F","S"];

const toISO = toLocalISODate;
function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }

/**
 * Booking.com-style live availability calendar.
 * Click a start date, then an end date, to select a check-in/check-out range.
 * roomId: scope to a specific room; omit for property-wide aggregate view.
 */
export default function AvailabilityCalendar({ roomId, checkIn, checkOut, onChange }) {
  const [monthOffset, setMonthOffset] = useState(0);
  const [days, setDays]     = useState({});
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(null); // holds picked start date while awaiting end date
  const [rangeBlockedNotice, setRangeBlockedNotice] = useState(false);

  const today = new Date(); today.setHours(0, 0, 0, 0);

  useEffect(() => {
    setLoading(true);
    const from = toISO(today);
    const to   = toISO(addDays(today, 120));
    const qs   = new URLSearchParams({ from, to, ...(roomId ? { roomId } : {}) });
    fetch(`${API}/availability-calendar?${qs}`)
      .then(r => r.json())
      .then(j => {
        const map = {};
        (j.data || []).forEach(d => { map[d.date] = d; });
        setDays(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [roomId]);

  /* A sold-out date can still be a CHECK-OUT date: a stay ending on the
     15th uses the nights up to the 14th, never the 15th itself. So once a
     check-in is picked, a struck-through date is allowed as the end — as
     long as every night before it is free. The server prices and checks
     only those nights, so this is what it would accept anyway. */
  function nightsFree(fromIso, toIso) {
    for (let c = new Date(fromIso); toISO(c) < toIso; c = addDays(c, 1)) {
      const d = days[toISO(c)];
      if (d && d.available === false) return false;
    }
    return true;
  }
  const canCheckOutOn = iso => !!selecting && iso > selecting && nightsFree(selecting, iso);

  function pick(dateStr) {
    const day = days[dateStr];
    if (day && day.available === false && !canCheckOutOn(dateStr)) return;
    setRangeBlockedNotice(false);
    if (!selecting || (checkIn && checkOut)) {
      setSelecting(dateStr);
      onChange(dateStr, "");
      return;
    }
    // Tapping the SAME date again completes a same-day (hourly-use) stay —
    // only an earlier date restarts the selection.
    if (dateStr === selecting) {
      onChange(selecting, dateStr);
      setSelecting(null);
      return;
    }
    if (dateStr < selecting) {
      setSelecting(dateStr);
      onChange(dateStr, "");
      return;
    }
    // Reject range if any day inside is unavailable
    let cursor = new Date(selecting);
    const end = new Date(dateStr);
    let blocked = false;
    while (cursor < end) {
      const iso = toISO(cursor);
      if (days[iso] && days[iso].available === false) blocked = true;
      cursor = addDays(cursor, 1);
    }
    if (blocked) {
      setRangeBlockedNotice(true);
      setSelecting(dateStr);
      onChange(dateStr, "");
      return;
    }
    onChange(selecting, dateStr);
    setSelecting(null);
  }

  function renderMonth(offset) {
    const base = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    const year = base.getFullYear(), month = base.getMonth();
    const firstDow = new Date(year, month, 1).getDay();
    const numDays  = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= numDays; d++) cells.push(new Date(year, month, d));

    return (
      <div className="flex-1 min-w-0 sm:min-w-[260px]">
        <p className="text-center font-bold text-gray-900 text-sm mb-3">
          {MONTH_NAMES[month]} {year}
        </p>
        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 text-center text-[10px] font-semibold text-gray-400 mb-1">
          {DOW.map((d, i) => <span key={i}>{d}</span>)}
        </div>
        <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
          {cells.map((date, i) => {
            if (!date) return <div key={i} />;
            const iso = toISO(date);
            const isPast = date < today;
            const day = days[iso];
            const isStart = iso === checkIn;
            const isEnd   = iso === checkOut;
            const soldOut = !isPast && day && day.available === false;
            // Sold out for the night, but fine to leave on (see canCheckOutOn).
            const checkoutOnly = soldOut && (isEnd || canCheckOutOn(iso));
            const unavailable = isPast || (soldOut && !checkoutOnly);
            const inRange = checkIn && checkOut && iso > checkIn && iso < checkOut;
            const isSelecting = iso === selecting;
            const reason = unavailable && !isPast ? blockedLabel(day?.blockedBy) : "";
            const isOtaBlocked = reason.startsWith("Blocked via");

            return (
              <button key={i} type="button" disabled={unavailable}
                onClick={() => pick(iso)}
                title={reason || (checkoutOnly ? "Check-out only — this night is booked" : day?.minPrice ? `₹${day.minPrice}/night` : "")}
                className={`aspect-square min-h-[44px] sm:min-h-0 rounded-lg text-[11px] flex flex-col items-center justify-center
                  transition-colors relative
                  ${unavailable ? "text-gray-300 cursor-not-allowed line-through" :
                    isStart || isEnd || isSelecting ? "bg-amber-500 text-white font-bold" :
                    inRange ? "bg-amber-100 text-amber-700" :
                    "hover:bg-amber-50 text-gray-700"}`}>
                <span>{date.getDate()}</span>
                {!unavailable && !checkoutOnly && day?.minPrice != null && (isStart || isEnd || inRange ? null : (
                  <span className="text-[8px] text-gray-400 leading-none">₹{Math.round(day.minPrice / 100) / 10}k</span>
                ))}
                {isOtaBlocked && (
                  <span className="absolute bottom-0.5 w-1 h-1 rounded-full bg-rose-300" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4">
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={() => setMonthOffset(o => Math.max(0, o - 1))} disabled={monthOffset === 0}
          className="tap-44 p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30">
          <ChevronLeft size={16} />
        </button>
        <span className="text-xs text-gray-400">
          {loading ? "Loading availability…" : "Tap a start date, then an end date"}
        </span>
        <button type="button" onClick={() => setMonthOffset(o => o + 1)}
          className="tap-44 p-1.5 rounded-lg hover:bg-gray-100">
          <ChevronRight size={16} />
        </button>
      </div>
      {rangeBlockedNotice && (
        <p className="text-[11px] text-rose-500 font-medium -mt-2 mb-3">
          That range includes a date that's already booked — selection restarted from your last tap.
        </p>
      )}
      <div className="flex gap-6 overflow-x-auto">
        {renderMonth(monthOffset)}
        <div className="hidden md:block">{renderMonth(monthOffset + 1)}</div>
      </div>
    </div>
  );
}
